import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import {
  getConsent,
  setConsent,
  onConsentBannerOpen,
  CONSENT_ACCEPTED,
  CONSENT_DECLINED,
} from '../utils/consent';

/**
 * Cookie-consent banner. Shows on every page when no choice is stored,
 * disappears permanently on either choice. Reopens on request from the
 * "Endre valg for informasjonskapsler" button on /privacy (§8) or
 * Account → Innstillinger (via openConsentBanner()).
 *
 * The two buttons look identical on purpose: declining must be as easy
 * and as visible as accepting (same as the coord.no banner).
 *
 * z-40 keeps it below the AuthPopup (z-50) so it can never cover it.
 */
const ConsentBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => !getConsent());

  useEffect(() => onConsentBannerOpen(() => setVisible(true)), []);

  if (!visible) return null;

  const choose = (choice) => {
    setConsent(choice);
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label={t('consent.ariaLabel')}
      className="fixed bottom-0 inset-x-0 z-40 bg-background-card border-t border-border-light shadow-lg px-4 py-4 md:px-8"
    >
      <div className="max-w-3xl mx-auto flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <p className="text-sm text-text-primary flex-1">
          {t('consent.message')}{' '}
          <Link
            to="/privacy"
            className="underline text-text-secondary hover:text-text-primary transition-colors duration-200"
          >
            {t('consent.privacyLink')}
          </Link>
        </p>
        <div className="flex gap-3 w-full md:w-auto">
          <button
            type="button"
            onClick={() => choose(CONSENT_DECLINED)}
            className="flex-1 md:flex-none md:w-28 min-h-[48px] px-6 rounded-lg bg-primary text-white font-semibold hover:bg-primary-600 transition-colors duration-200"
          >
            {t('consent.decline')}
          </button>
          <button
            type="button"
            onClick={() => choose(CONSENT_ACCEPTED)}
            className="flex-1 md:flex-none md:w-28 min-h-[48px] px-6 rounded-lg bg-primary text-white font-semibold hover:bg-primary-600 transition-colors duration-200"
          >
            {t('consent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
